export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The generator writes methods as a comma-joined list, or CRUD for a full crud controller. */
type Expand<M extends string> =
  M extends 'CRUD' ? HttpMethod
  : M extends `${infer First},${infer Rest}` ? Expand<First> | Expand<Rest>
  : M extends HttpMethod ? M : never;

/** A resolved endpoint url, carrying the methods it accepts as a phantom brand. */
export type Url<M extends string> = string & { readonly __methods?: Record<Expand<M>, true> };

export type ApiNode = Url<string> | { readonly [key: string]: ApiNode };

/**
 * The generated literal holds each endpoint's HTTP methods; buildApi replaces them with urls at
 * load time. Deriving the resolved shape from that literal keeps the methods checkable without a
 * second copy of the tree written out as a type.
 */
export type Resolved<T> = T extends string ? Url<T> : { readonly [K in keyof T]: Resolved<T[K]> };

/**
 * An endpoint reachable with M: either the url itself or the node carrying it under `_`. The brand
 * is an optional property, so a plain string url stays acceptable - only a generated endpoint that
 * declares its methods and omits M is rejected. Requiring a method the endpoint lacks is the error
 * worth catching; a namespace node has no `_` and is matched by the index signature.
 */
export type Endpoint<M extends HttpMethod> =
  | (string & { readonly __methods?: Record<M, true> })
  | { readonly _?: string & { readonly __methods?: Record<M, true> }, readonly [key: string]: unknown };

/**
 * The endpoint a `.one(node, id)` call actually reaches, which is `node/{id}` rather than the node.
 * The path variable child is what has to accept M; the node's own methods describe the collection.
 */
export type ChildEndpoint<M extends HttpMethod> =
  | string
  | ({ readonly [K in `$${string}`]?: Endpoint<M> } & { readonly [key: string]: unknown });

type MutableNode = Record<string, unknown>;

const ROOT = '_';

/** Resolves the tree in place: every leaf becomes its url, every node stringifies to its own. */
export function buildApi<T extends object>(schema: T): Resolved<T>;
export function buildApi(schema: object) {
  mapKeysToUrls(schema as MutableNode);
  return schema;
}

function mapKeysToUrls(object: MutableNode, parentUrl = '') {
  Object.defineProperty(object, 'toString', { value: () => object[ROOT] || parentUrl });

  for (const key in object) {
    const url = parentUrl + mapKeyToUrl(key);
    const value = object[key];

    if (typeof value === 'string') {
      object[key] = url;
    } else {
      mapKeysToUrls(value as MutableNode, url);
    }
  }
}

function mapKeyToUrl(key: string) {

  if (key === ROOT) {
    return '';
  }

  return '/' + key
    .replace(new RegExp('\\$', 'g'), ':')
    .replace(new RegExp('__', 'g'), '.')
    .replace(new RegExp('_', 'g'), '-');
}

// enums can be array also
export function buildEnums<T extends object>(enums: T) {

  for (const enumName in enums) {

    const enumObject = enums[enumName] as MutableNode;

    for (const enumValue in enumObject) {
      if (enumValue[0] !== '_') {
        enumObject[enumValue] = enumValue;
      }
    }

  }
}

export function groupBy<T>(input: T[], key: keyof T) {
  return input.reduce<Record<string, T[]>>((acc, currentValue) => {
    const groupKey = String(currentValue[key]);
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(currentValue);
    return acc;
  }, {});
}

export function toObject<T>(input: T[], key: keyof T) {
  return input.reduce<Record<string, T>>((acc, currentValue) => {
    acc[String(currentValue[key])] = { ...currentValue };
    return acc;
  }, {});
}

export const mapReducer = (key: string, clone = false) =>
  <T>(memo: Record<string, T>, item: T) => {
    memo[String((item as MutableNode)[key])] = clone ? { ...item } : item;
    return memo;
  };
